/**
 * A Web Audio Module as an instrument.
 *
 * A WAM that makes sound takes its notes as `wam-midi` events on the same
 * node the host builds for an effect, at the sample they are due, so hosting
 * one as an instrument is the effect host with `noteOn` and `noteOff` that
 * schedule MIDI bytes. The pedal never reaches here — the store's pedal
 * decides which releases are passed on — and the wheels arrive as the
 * parameters every instrument has, `bend` and `modWheel`, which leave as the
 * pitch bend and CC1 a keyboard would have sent.
 *
 * Until the plugin has loaded there is nothing to play: a note that arrives
 * first is dropped, as a sampler zone's is before its audio is decoded. A
 * render waits for the plugin before it schedules a note, so a render never
 * drops one.
 */
import type { InstrumentState, InstrumentType, WamParameterInfo } from '@/domain/types';
import type { AutomatableParam } from '../effects/EffectNode';
import { WamParamProxy, restoreWam, tellWam } from '../effects/EffectNode';
import type { TransportTell } from '../wamTransport';
import { loadWam, type LoadedWam } from '../effects/wamHost';
import { defaultInstrumentParams } from './descriptors';
import { rampParam, registerInstrument, type InstrumentNode } from './Instrument';
import { DEFAULT_BEND_RANGE_SEMITONES } from '@/storage/midiPerformance';

/** The wheel at rest, in the fourteen bits pitch bend is sent as. */
const BEND_CENTRE = 8192;

class WamInstrument implements InstrumentNode {
  readonly type: InstrumentType = 'wam';
  readonly output: GainNode;
  readonly ready: Promise<void>;
  readonly wamInfo: Promise<{ name: string; parameters: WamParameterInfo[] }>;

  private loaded: LoadedWam | null = null;
  private disposed = false;
  private params: Record<string, number>;
  private readonly wanted = new Map<string, number>();
  private readonly proxies = new Map<string, WamParamProxy>();

  constructor(
    private readonly ctx: BaseAudioContext,
    url: string,
    savedState: unknown,
  ) {
    this.params = defaultInstrumentParams('wam');
    this.output = ctx.createGain();
    this.output.gain.value = this.params.volume;

    this.wamInfo = loadWam(ctx, url).then((result) => {
      if (this.disposed) {
        result.node.destroy();
        throw new Error('disposed');
      }
      this.loaded = result;
      result.node.connect(this.output);
      return restoreWam(result, savedState, this.wanted).then(() => {
        this.sendWheels(ctx.currentTime);
        return { name: result.name, parameters: result.parameters };
      });
    });
    this.ready = this.wamInfo.then(
      () => undefined,
      (error: unknown) => {
        if (this.disposed) return;
        console.warn(`The instrument at ${url} could not be loaded; its track is silent.`, error);
      },
    );
  }

  /** MIDI bytes to the plugin, at a time on the context's clock. */
  private midi(bytes: [number, number, number], when: number): void {
    this.loaded?.node.scheduleEvents({ type: 'wam-midi', time: when, data: { bytes } });
  }

  /** The wheels as the bytes a keyboard sends: pitch bend over the default range, and CC1. */
  private sendWheels(when: number): void {
    const bend = Math.max(-1, Math.min(1, (this.params.bend ?? 0) / DEFAULT_BEND_RANGE_SEMITONES));
    const raw = Math.max(0, Math.min(16383, Math.round(BEND_CENTRE + bend * (bend < 0 ? BEND_CENTRE : BEND_CENTRE - 1))));
    this.midi([0xe0, raw & 0x7f, raw >> 7], when);
    this.midi([0xb0, 1, Math.max(0, Math.min(127, Math.round((this.params.modWheel ?? 0) * 127)))], when);
  }

  playNote(pitch: number, velocity: number, when: number, duration: number): void {
    this.noteOn(pitch, velocity, when);
    this.noteOff(pitch, when + Math.max(0.005, duration));
  }

  noteOn(pitch: number, velocity: number, when: number): void {
    const p = Math.max(0, Math.min(127, Math.round(pitch)));
    const v = Math.max(1, Math.min(127, Math.round(velocity)));
    this.midi([0x90, p, v], when);
  }

  noteOff(pitch: number, when: number): void {
    this.midi([0x80, Math.max(0, Math.min(127, Math.round(pitch))), 0], when);
  }

  allNotesOff(when: number): void {
    // All notes off, and all sound off after it, for a plugin that reads one
    // and not the other.
    this.midi([0xb0, 123, 0], when);
    this.midi([0xb0, 120, 0], when);
  }

  setParam(paramId: string, value: number, atTime?: number): void {
    this.params[paramId] = value;
    switch (paramId) {
      case 'volume':
        rampParam(this.output.gain, value, this.ctx, atTime);
        return;
      case 'bend':
      case 'modWheel':
        if (this.loaded) this.sendWheels(atTime ?? this.ctx.currentTime);
        return;
      default:
        this.wanted.set(paramId, value);
        if (this.loaded) void this.loaded.node.setParameterValues({ [paramId]: { id: paramId, value, normalized: false } });
    }
  }

  paramTargets(paramId: string): AutomatableParam[] {
    if (paramId === 'volume') return [{ param: this.output.gain, map: (v) => v }];
    if (!this.loaded || !this.loaded.parameters.some((p) => p.id === paramId)) return [];
    let proxy = this.proxies.get(paramId);
    if (!proxy) {
      proxy = new WamParamProxy(this.loaded.node, paramId, this.wanted.get(paramId) ?? 0);
      this.proxies.set(paramId, proxy);
    }
    return [{ param: proxy, map: (v) => v, nonlinear: true }];
  }

  setState(_state: InstrumentState): void {
    // The plugin's URL is fixed for the life of the node: a different URL is
    // a different instrument, and the graph rebuilds it.
  }

  async createGui(): Promise<Element> {
    await this.wamInfo;
    if (!this.loaded) throw new Error('The plugin is not loaded.');
    return this.loaded.instance.createGui();
  }

  destroyGui(gui: Element): void {
    this.loaded?.instance.destroyGui(gui);
  }

  async captureState(): Promise<unknown> {
    return this.loaded ? this.loaded.node.getState() : null;
  }

  tellTransport(tells: readonly TransportTell[]): void {
    if (this.loaded) tellWam(this.loaded, tells);
  }

  latencySec(): number {
    return this.loaded?.latencySec ?? 0;
  }

  dispose(): void {
    this.disposed = true;
    if (this.loaded) {
      this.loaded.node.disconnect();
      this.loaded.node.destroy();
      this.loaded = null;
    }
    this.output.disconnect();
  }
}

/** Where a track's plugin comes from, as the instrument state carries it. */
export function wamInstrumentUrl(state: InstrumentState): string | null {
  const url = state.data.wamUrl;
  return typeof url === 'string' && url.length > 0 ? url : null;
}

registerInstrument('wam', (ctx, _resolveBuffer, state) => new WamInstrument(ctx, (state && wamInstrumentUrl(state)) ?? '', state?.data.wamState));
